var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import format from 'string-format';
import { decodeCreateBundleResponse, decodeReadBundlesResponse } from '../../../types/lighthouse/decoders/bundles';
import { decodeDeleteBundleResponse, decodeReadBundleResponse, decodeUpdateBundleResponse } from '../../../types/lighthouse/decoders/bundles/bundle-id';
import { useLighthouseFetch } from '@/hooks/use-lighthouse-fetch';
export var BUNDLES_PATH = '/bundles';
export function formatBundlePath(bundle_id) {
    if (bundle_id === void 0) { bundle_id = ':bundle_id'; }
    return "".concat(BUNDLES_PATH, "/").concat(bundle_id);
}
/**
 * Hook to fetch list of bundles
 */
export function useBundles() {
    var _this = this;
    var _a = useLighthouseFetch(), executeFetch = _a.executeFetch, decodeResponse = _a.decodeResponse, handleCommonStatusCodes = _a.handleCommonStatusCodes;
    var queryClient = useQueryClient();
    var t = useTranslation().t;
    var fetchBundles = useCallback(function (context) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(BUNDLES_PATH, __assign({}, context))];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeReadBundlesResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    handleCommonStatusCodes(data, status, t('api.bundles.get'));
                    if (status === 200) {
                        data.bundles.forEach(function (bundle) {
                            queryClient.setQueryData([BUNDLES_PATH, bundle.id], bundle);
                        });
                        return [2 /*return*/, data];
                    }
                    return [2 /*return*/, null];
            }
        });
    }); }, [decodeResponse, executeFetch, handleCommonStatusCodes, queryClient, t]);
    return useQuery({
        queryKey: [BUNDLES_PATH],
        queryFn: fetchBundles,
    });
}
/**
 * Hook to fetch Bundle by ID
 */
export function useBundleById(bundleId, _a) {
    var _this = this;
    var _b = _a === void 0 ? {} : _a, notFoundCallback = _b.notFoundCallback;
    var _c = useLighthouseFetch(), executeFetch = _c.executeFetch, decodeResponse = _c.decodeResponse, handleCommonStatusCodes = _c.handleCommonStatusCodes;
    var t = useTranslation().t;
    var fetchBundle = useCallback(function (context) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status, translations;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(formatBundlePath(bundleId), __assign({}, context))];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeReadBundleResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    translations = __assign(__assign({}, t('api.bundles.get')), { not_found: { message: format(t('api.bundles.get.not_found.message'), bundleId) } });
                    handleCommonStatusCodes(data, status, translations);
                    if ((status === 403 || status === 404) && notFoundCallback) {
                        notFoundCallback(status);
                        return [2 /*return*/, null];
                    }
                    return [2 /*return*/, status === 200 ? data.bundle : null];
            }
        });
    }); }, [executeFetch, bundleId, decodeResponse, t, handleCommonStatusCodes, notFoundCallback]);
    return useQuery({
        queryKey: [BUNDLES_PATH, bundleId],
        queryFn: fetchBundle,
    });
}
/**
 * Hook to create new enrollment bundle
 */
export function useAddBundle() {
    var _this = this;
    var _a = useLighthouseFetch(), executeFetch = _a.executeFetch, decodeResponse = _a.decodeResponse, handleCommonStatusCodes = _a.handleCommonStatusCodes;
    var t = useTranslation().t;
    var queryClient = useQueryClient();
    return useCallback(function (bundle) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(BUNDLES_PATH, { method: 'POST', body: JSON.stringify({ bundle: bundle }) })];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeCreateBundleResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    handleCommonStatusCodes(data, status, t('api.bundles.post'));
                    if (status === 200) {
                        queryClient
                            .invalidateQueries({
                            queryKey: [BUNDLES_PATH],
                        })
                            .catch(console.error);
                        queryClient.setQueryData([BUNDLES_PATH, data.bundle.id], data.bundle);
                        return [2 /*return*/, data.bundle];
                    }
                    return [2 /*return*/, null];
            }
        });
    }); }, [executeFetch, decodeResponse, handleCommonStatusCodes, t, queryClient]);
}
/**
 * Hook to update enrollment bundle
 */
export function useUpdateBundle() {
    var _this = this;
    var t = useTranslation().t;
    var _a = useLighthouseFetch(), executeFetch = _a.executeFetch, decodeResponse = _a.decodeResponse, handleCommonStatusCodes = _a.handleCommonStatusCodes;
    var queryClient = useQueryClient();
    return useCallback(function (_a) { return __awaiter(_this, void 0, void 0, function () {
        var response, _b, data, status, translations;
        var bundleId = _a.id, bundle = __rest(_a, ["id"]);
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0: return [4 /*yield*/, executeFetch(formatBundlePath(bundleId), { method: 'PUT', body: JSON.stringify({ bundle: bundle }) })];
                case 1:
                    response = _c.sent();
                    return [4 /*yield*/, decodeResponse(decodeUpdateBundleResponse, response)];
                case 2:
                    _b = _c.sent(), data = _b.data, status = _b.status;
                    translations = __assign(__assign({}, t('api.bundles.put')), { error: { message: format(t('api.bundles.put.error.message'), bundleId) }, forbidden: { message: format(t('api.bundles.put.forbidden.message'), bundleId) } });
                    handleCommonStatusCodes(data, status, translations);
                    if (status === 200) {
                        queryClient
                            .invalidateQueries({
                            queryKey: [BUNDLES_PATH],
                        })
                            .catch(console.error);
                        queryClient.setQueryData([BUNDLES_PATH, data.bundle.id], data.bundle);
                        return [2 /*return*/, data.bundle];
                    }
                    return [2 /*return*/, null];
            }
        });
    }); }, [decodeResponse, executeFetch, handleCommonStatusCodes, queryClient, t]);
}
/**
 * Hook to delete enrollment bundle
 */
export function useDeleteBundle() {
    var _this = this;
    var t = useTranslation().t;
    var _a = useLighthouseFetch(), executeFetch = _a.executeFetch, decodeResponse = _a.decodeResponse, handleCommonStatusCodes = _a.handleCommonStatusCodes;
    var queryClient = useQueryClient();
    return useCallback(function (bundleId) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status, translations;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(formatBundlePath(bundleId), { method: 'DELETE' })];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeDeleteBundleResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    translations = __assign(__assign({}, t('api.bundles.delete')), { error: { message: format(t('api.bundles.delete.error.message'), bundleId) }, forbidden: { message: format(t('api.bundles.delete.forbidden.message'), bundleId) }, not_found: { message: format(t('api.bundles.delete.not_found.message'), bundleId) } });
                    handleCommonStatusCodes(data, status, translations);
                    if (status === 200) {
                        queryClient
                            .invalidateQueries({
                            queryKey: [BUNDLES_PATH],
                            refetchType: 'none',
                        })
                            .catch(console.error);
                        queryClient.removeQueries({
                            queryKey: [BUNDLES_PATH, bundleId],
                        });
                        return [2 /*return*/, true];
                    }
                    return [2 /*return*/, false];
            }
        });
    }); }, [executeFetch, decodeResponse, queryClient, t, handleCommonStatusCodes]);
}
